import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  Package,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowRight,
  Activity,
  Users,
  DollarSign,
  CreditCard,
  Calendar,
  Award,
  Zap,
  RefreshCw,
  Wrench,
  Building2,
  AlertTriangle,
  X,
  ExternalLink
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { workOrderService } from '../../services/workOrderService';
import { pmScheduleService } from '../../services/pmScheduleService';
import { assetService } from '../../services/assetService';
import { authService } from '../../services/authService';
import { subscriptionService } from '../../services/subscriptionService';
import { chatbotService } from '../../services/chatbotService';
import { teamService } from '../../services/teamService';
import { useDateFormat } from '../../hooks/useDateFormat';

const Dashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [cmmsData, setCmmsData] = useState({
    stats: {},
    recentWorkOrders: [],
    upcomingMaintenance: [],
    totalAssets: 0
  });
  const [subscriptions, setSubscriptions] = useState({
    cmms: null,
    chatbot: null
  });
  const [chatbotStats, setChatbotStats] = useState(null);
  const [teamMemberCount, setTeamMemberCount] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingService, setCancellingService] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const { formatDate } = useDateFormat();

  // Check user role - Dashboard is only for COMPANY_OWNER
  const companyRole = localStorage.getItem('companyRole');
  const isCompanyOwner = companyRole === 'COMPANY_OWNER';

  // Check subscription status
  const hasCmmsSubscription = localStorage.getItem('hasCmmsSubscription') === 'true';
  const hasChatbotSubscription = localStorage.getItem('hasChatbotSubscription') === 'true';
  const user = authService.getCurrentUser();

  useEffect(() => {
    // Only fetch data if user is company owner
    if (isCompanyOwner) {
      fetchAllData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  }, [isCompanyOwner]);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      // Fetch subscription details
      const subs = await subscriptionService.getAllSubscriptions();
      setSubscriptions(subs);

      // Fetch team members count (CMMS only)
      if (hasCmmsSubscription) {
        teamService.getTeamMembers()
          .then((members) => setTeamMemberCount((members || []).length))
          .catch(() => {});
      }

      // Fetch chatbot stats if subscribed
      if (hasChatbotSubscription) {
        chatbotService.getDashboardStats()
          .then((s) => setChatbotStats(s))
          .catch(() => {});
      }

      // Fetch CMMS data if subscribed
      if (hasCmmsSubscription) {
        const [stats, workOrders, upcomingPM, assets] = await Promise.all([
          workOrderService.getStats().catch(() => ({})),
          workOrderService.getWorkOrders().catch(() => []),
          pmScheduleService.getUpcomingSchedules(7).catch(() => []),
          assetService.getAssets().catch(() => [])
        ]);

        // Get recent work orders (latest 4, not completed)
        const recentWOs = (workOrders || [])
          .filter(wo => wo.status !== 'COMPLETED' && wo.status !== 'CANCELLED')
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 4);

        setCmmsData({
          stats: stats || {},
          recentWorkOrders: recentWOs,
          upcomingMaintenance: (upcomingPM || []).slice(0, 3),
          totalAssets: (assets || []).length
        });
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTrial = async () => {
    if (!cancellingService) return;

    try {
      setCancelling(true);
      if (cancellingService === 'cmms') {
        await subscriptionService.cancelCmmsSubscription();
        localStorage.setItem('hasCmmsSubscription', 'false');
      } else if (cancellingService === 'chatbot') {
        await subscriptionService.cancelChatbotSubscription();
        localStorage.setItem('hasChatbotSubscription', 'false');
      }
      setShowCancelModal(false);
      setCancellingService(null);
      // Refresh data
      window.location.reload();
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  // Build stats based on subscriptions
  const buildStats = () => {
    const stats = [];

    if (hasCmmsSubscription) {
      stats.push({
        name: t('dashboard.totalAssets'),
        value: cmmsData.totalAssets.toString(),
        change: t('dashboard.managedAssets'),
        trend: 'up',
        icon: Package,
        color: 'bg-blue-500',
        href: '/portal/cmms/assets'
      });
      stats.push({
        name: t('dashboard.activeWorkOrders'),
        value: (cmmsData.stats.active || 0).toString(),
        change: `${cmmsData.stats.inProgress || 0} ${t('dashboard.inProgress')}`,
        trend: cmmsData.stats.active > 0 ? 'warning' : 'up',
        icon: Activity,
        color: 'bg-green-500',
        href: '/portal/cmms/work-orders'
      });
      stats.push({
        name: t('dashboard.teamMembers'),
        value: teamMemberCount !== null ? teamMemberCount.toLocaleString() : '—',
        change: t('dashboard.viewTeam'),
        trend: 'up',
        icon: Users,
        color: 'bg-orange-500',
        href: '/portal/cmms/team'
      });
    }

    if (hasChatbotSubscription) {
      stats.push({
        name: t('dashboard.chatbotConversations'),
        value: chatbotStats ? (chatbotStats.totalConversations ?? 0).toLocaleString() : '—',
        change: chatbotStats ? `${(chatbotStats.totalMessages ?? 0).toLocaleString()} ${t('dashboard.messages')}` : t('common.loading'),
        trend: 'up',
        icon: MessageSquare,
        color: 'bg-purple-500',
        href: '/portal/chatbot'
      });
    }

    return stats;
  };

  // Build quick actions based on subscriptions
  const buildQuickActions = () => {
    const actions = [];

    if (hasCmmsSubscription) {
      actions.push({ name: t('dashboard.createWorkOrder'), href: '/portal/cmms/work-orders', icon: Activity, color: 'bg-blue-500' });
      actions.push({ name: t('dashboard.addAsset'), href: '/portal/cmms/assets', icon: Package, color: 'bg-green-500' });
      actions.push({ name: t('dashboard.viewReports'), href: '/portal/cmms/reports', icon: TrendingUp, color: 'bg-purple-500' });
      actions.push({ name: t('dashboard.manageTeam'), href: '/portal/cmms/team', icon: Users, color: 'bg-orange-500' });
    }

    return actions;
  };

  const stats = buildStats();
  const quickActions = buildQuickActions();

  // Helper functions
  const getStatusColor = (status) => {
    const statusUpper = status?.toUpperCase?.() || status;
    switch (statusUpper) {
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
      case 'OPEN':
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'ASSIGNED': return 'bg-indigo-100 text-indigo-800';
      case 'ON_HOLD': return 'bg-orange-100 text-orange-800';
      case 'PENDING_REVIEW': return 'bg-purple-100 text-purple-800';
      case 'SCHEDULED': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    const priorityUpper = priority?.toUpperCase?.() || priority;
    switch (priorityUpper) {
      case 'URGENT': return 'bg-red-100 text-red-800';
      case 'HIGH': return 'bg-orange-100 text-orange-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'LOW': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Subscription card component
  const SubscriptionCard = ({ service, subscription, hasSubscription }) => {
    const isTrial = subscription?.status === 'TRIAL';
    const trialDaysRemaining = isTrial && subscription?.trialEndDate
      ? subscriptionService.calculateTrialDaysRemaining(subscription.trialEndDate)
      : null;
    const isTrialExpired = trialDaysRemaining !== null && trialDaysRemaining < 0;
    const isCancelled = subscription?.status === 'CANCELLED';
    // Covers the case where the scheduler already flipped status to EXPIRED/SUSPENDED
    const isExpired = subscription?.status === 'EXPIRED' || subscription?.status === 'SUSPENDED';

    const Icon = service === 'cmms' ? Wrench : MessageSquare;
    const serviceName = service === 'cmms' ? t('nav.cmms') : t('nav.chatbot');
    const serviceDescription = service === 'cmms'
      ? t('products.cmmsFullName')
      : t('products.chatbotDescription');

    if (!hasSubscription) {
      return (
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon className="w-5 h-5" />
            <span className="font-semibold">{serviceName}</span>
          </div>
          <div className="text-sm text-blue-100 mb-3">{serviceDescription}</div>
          <Link
            to="/portal/products"
            className="inline-flex items-center gap-1 text-sm text-white bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors"
          >
            {t('dashboard.subscribeNow')}
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      );
    }

    return (
      <div className={`backdrop-blur-sm rounded-lg p-4 ${
        isExpired || isTrialExpired || isCancelled ? 'bg-red-500/20' : isTrial ? 'bg-yellow-500/20' : 'bg-white/20'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-5 h-5" />
          <span className="font-semibold">{serviceName}</span>
          {isCancelled ? (
            <span className="ml-auto bg-red-400/30 text-red-100 text-xs px-2 py-0.5 rounded-full">{t('dashboard.cancelled')}</span>
          ) : isExpired ? (
            <span className="ml-auto bg-red-400/30 text-red-100 text-xs px-2 py-0.5 rounded-full">{t('billing.statusExpired')}</span>
          ) : isTrialExpired ? (
            <span className="ml-auto bg-red-400/30 text-red-100 text-xs px-2 py-0.5 rounded-full">{t('dashboard.trialExpired')}</span>
          ) : isTrial ? (
            <span className="ml-auto bg-yellow-400/30 text-yellow-100 text-xs px-2 py-0.5 rounded-full">{t('dashboard.trial')}</span>
          ) : (
            <span className="ml-auto bg-green-400/30 text-green-100 text-xs px-2 py-0.5 rounded-full">{t('common.active')}</span>
          )}
        </div>

        {isTrial && !isCancelled && (
          <div className="mb-3">
            {isTrialExpired ? (
              <div className="flex items-center gap-2 text-red-200 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>{t('dashboard.trialExpiredDaysAgo', { count: Math.abs(trialDaysRemaining) })}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-yellow-200 text-sm">
                <Clock className="w-4 h-4" />
                <span>
                  {trialDaysRemaining === 0
                    ? t('dashboard.trialEndsToday')
                    : trialDaysRemaining === 1
                    ? t('dashboard.dayRemaining')
                    : t('dashboard.daysRemaining', { count: trialDaysRemaining })}
                </span>
              </div>
            )}
            <div className="text-xs text-blue-200 mt-1">
              {t('dashboard.expires')}: {formatDate(subscription.trialEndDate)}
            </div>
          </div>
        )}

        {!isTrial && !isCancelled && subscription && (
          <div className="text-sm text-blue-100 mb-3">
            <div>{t('dashboard.planName')}: {subscription.planName || 'Standard'}</div>
            <div className="text-xs text-blue-200 mt-1">
              {t('nav.billing')}: {subscription.billingCycle === 'ANNUAL' ? t('dashboard.billingAnnual') : t('dashboard.billingMonthly')}
            </div>
          </div>
        )}

        {isCancelled ? (
          <Link
            to="/portal/products"
            className="inline-flex items-center gap-1 text-sm text-white bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors"
          >
            {t('dashboard.subscribeAgain')}
            <ArrowRight className="w-3 h-3" />
          </Link>
        ) : isExpired ? (
          <Link
            to={`/portal/billing?upgrade=${service.toUpperCase()}`}
            className="inline-flex items-center gap-1 text-sm text-white bg-green-500/50 hover:bg-green-500/70 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Zap className="w-3 h-3" />
            {t('billing.renewNow')}
          </Link>
        ) : isTrial || isTrialExpired ? (
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/portal/billing?upgrade=${service.toUpperCase()}`}
              className="inline-flex items-center gap-1 text-sm text-white bg-green-500/50 hover:bg-green-500/70 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Zap className="w-3 h-3" />
              {t('dashboard.upgradeToPaid')}
            </Link>
            <button
              onClick={() => {
                setCancellingService(service);
                setShowCancelModal(true);
              }}
              className="inline-flex items-center gap-1 text-sm text-red-200 hover:text-white bg-red-500/20 hover:bg-red-500/40 px-3 py-1.5 rounded-lg transition-colors"
            >
              <X className="w-3 h-3" />
              {t('dashboard.cancelTrial')}
            </button>
          </div>
        ) : (
          <Link
            to="/portal/billing"
            className="inline-flex items-center gap-1 text-sm text-white hover:underline"
          >
            {t('dashboard.manageSubscription')}
            <ExternalLink className="w-3 h-3" />
          </Link>
        )}
      </div>
    );
  };

  // Redirect non-owners to CMMS Overview (if they have CMMS) or show restricted message
  if (!isCompanyOwner) {
    if (hasCmmsSubscription) {
      return <Navigate to="/portal/cmms" replace />;
    }
    // For users without CMMS subscription, show a restricted access message
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-yellow-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('dashboard.accessRestricted')}</h2>
          <p className="text-gray-600 mb-4">
            {t('dashboard.accessRestrictedMsg')}
          </p>
          <Link
            to="/portal/account"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
          >
            {t('dashboard.goToAccountSettings')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.cancelTrial_title')}</h3>
            </div>
            <p className="text-gray-600 mb-6">
              {t('dashboard.cancelTrialConfirm', { service: cancellingService === 'cmms' ? t('nav.cmms') : t('nav.chatbot') })}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancellingService(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={cancelling}
              >
                {t('dashboard.keepTrial')}
              </button>
              <button
                onClick={handleCancelTrial}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
                disabled={cancelling}
              >
                {cancelling ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    {t('dashboard.cancelling')}
                  </>
                ) : (
                  t('dashboard.yesCancelTrial')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <p className="text-gray-600 mt-1">
            {t('dashboard.welcome')}{user?.firstName ? `, ${user.firstName}` : ''}! {t('dashboard.hereWhatHappening')}
          </p>
        </div>
        <button
          onClick={fetchAllData}
          className="btn btn-outline flex items-center gap-2"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('dashboard.refresh')}
        </button>
      </div>

      {/* Trial Expiration Warning */}
      {(subscriptions.cmms?.status === 'TRIAL' || subscriptions.chatbot?.status === 'TRIAL') && (
        <div className="space-y-3">
          {subscriptions.cmms?.status === 'TRIAL' && (() => {
            const daysRemaining = subscriptionService.calculateTrialDaysRemaining(subscriptions.cmms.trialEndDate);
            const isExpired = daysRemaining !== null && daysRemaining < 0;
            const isUrgent = daysRemaining !== null && daysRemaining <= 3;

            if (isExpired || isUrgent) {
              return (
                <div className={`border-2 rounded-xl p-4 ${
                  isExpired ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'
                }`}>
                  <div className="flex items-center gap-3">
                    <AlertTriangle className={`w-6 h-6 ${isExpired ? 'text-red-600' : 'text-yellow-600'}`} />
                    <div className="flex-1">
                      <h3 className={`font-semibold ${isExpired ? 'text-red-900' : 'text-yellow-900'}`}>
                        {isExpired
                          ? t('dashboard.cmmsTrialExpired')
                          : daysRemaining === 0
                          ? t('dashboard.cmmsTrialEndsToday')
                          : t('dashboard.cmmsTrialEndsDays', { count: daysRemaining })
                        }
                      </h3>
                      <p className={`text-sm ${isExpired ? 'text-red-700' : 'text-yellow-700'}`}>
                        {isExpired
                          ? t('dashboard.subscribeToContinue')
                          : t('dashboard.upgradeToContinue')
                        }
                      </p>
                    </div>
                    <Link
                      to="/portal/products"
                      className={`px-4 py-2 rounded-lg font-medium ${
                        isExpired
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-yellow-600 text-white hover:bg-yellow-700'
                      }`}
                    >
                      {t('dashboard.subscribeNow')}
                    </Link>
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* No subscriptions message */}
      {!hasCmmsSubscription && !hasChatbotSubscription && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-800">{t('dashboard.noActiveSubscriptions')}</h3>
              <p className="text-yellow-700 mt-1">
                {t('dashboard.noSubscriptionsMsg')}
              </p>
              <Link
                to="/portal/products"
                className="inline-flex items-center gap-2 mt-3 text-yellow-800 font-medium hover:text-yellow-900"
              >
                {t('dashboard.viewProducts')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {stats.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link
                key={stat.name}
                to={stat.href}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  {stat.trend === 'up' && <TrendingUp className="w-5 h-5 text-green-500" />}
                  {stat.trend === 'down' && <TrendingDown className="w-5 h-5 text-red-500" />}
                  {stat.trend === 'warning' && <AlertCircle className="w-5 h-5 text-orange-500" />}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.name}</div>
                <div className="text-xs text-gray-500 mt-1">{stat.change}</div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Subscription Overview */}
      <div className="bg-gradient-to-br from-primary-600 to-blue-700 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-6 h-6" />
              <h2 className="text-2xl font-bold">{t('dashboard.yourSubscriptions')}</h2>
            </div>
            <p className="text-blue-100">{t('dashboard.manageServices')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* CMMS Subscription Card */}
          <SubscriptionCard
            service="cmms"
            subscription={subscriptions.cmms}
            hasSubscription={hasCmmsSubscription}
          />

          {/* Chatbot Subscription Card */}
          <SubscriptionCard
            service="chatbot"
            subscription={subscriptions.chatbot}
            hasSubscription={hasChatbotSubscription}
          />

          {/* Billing Info */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-5 h-5" />
              <span className="font-semibold">{t('nav.billing')}</span>
            </div>
            <div className="text-sm text-blue-100 mb-3">{t('dashboard.manageBillingDesc')}</div>
            <Link
              to="/portal/billing"
              className="inline-flex items-center gap-1 text-sm text-white hover:underline"
            >
              {t('dashboard.viewBilling')}
              <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>

        <div className="flex items-center justify-end">
          <div className="flex gap-3">
            <Link
              to="/portal/billing"
              className="px-4 py-2 bg-white text-primary-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            >
              {t('dashboard.manageBilling')}
            </Link>
            <Link
              to="/portal/products"
              className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg font-medium hover:bg-white/30 transition-colors flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              {t('dashboard.exploreServices')}
            </Link>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">{t('dashboard.quickActions')}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <Link
                  key={action.name}
                  to={action.href}
                  className="flex flex-col items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
                >
                  <div className={`w-12 h-12 ${action.color} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-900 text-center">{action.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* CMMS Widgets - Only show if subscribed */}
      {hasCmmsSubscription && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Work Orders */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">{t('dashboard.recentWorkOrders')}</h2>
              <Link to="/portal/cmms/work-orders" className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center gap-1">
                {t('common.viewAll')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-16 bg-gray-100 rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : cmmsData.recentWorkOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                <p>{t('dashboard.noActiveWorkOrders')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cmmsData.recentWorkOrders.map((order) => (
                  <Link
                    key={order.id}
                    to={`/portal/cmms/work-orders/${order.id}`}
                    className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow block"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className="font-medium text-gray-900">{order.woNumber}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {order.status?.replace('_', ' ')}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(order.priority)}`}>
                          {order.priority}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">{order.title}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {order.assetName} • {order.assignedToName || 'Unassigned'}
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Maintenance */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">{t('dashboard.upcomingMaintenance')}</h2>
              <Link to="/portal/cmms/pm-schedules" className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center gap-1">
                {t('common.viewAll')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-16 bg-gray-100 rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : cmmsData.upcomingMaintenance.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{t('dashboard.noUpcomingMaintenance')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cmmsData.upcomingMaintenance.map((item) => (
                  <Link
                    key={item.id}
                    to="/portal/cmms/pm-schedules"
                    className="flex items-start gap-4 p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow block"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      item.maintenanceType === 'INTERNAL' ? 'bg-blue-100' : 'bg-purple-100'
                    }`}>
                      {item.maintenanceType === 'INTERNAL' ? (
                        <Wrench className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Building2 className="w-5 h-5 text-purple-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-gray-900">{item.name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.maintenanceType === 'INTERNAL'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {item.maintenanceType === 'INTERNAL' ? 'Work Order' : 'Visit'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">{item.assetName}</div>
                      <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        {formatDate(item.nextDueDate)}
                        {item.daysUntilDue !== undefined && (
                          <span className={`${
                            item.daysUntilDue < 0
                              ? 'text-red-600'
                              : item.daysUntilDue <= 3
                              ? 'text-yellow-600'
                              : 'text-gray-500'
                          }`}>
                            ({item.daysUntilDue < 0
                              ? t('dashboard.daysOverdue', { count: Math.abs(item.daysUntilDue) })
                              : t('dashboard.days', { count: item.daysUntilDue })})
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* System Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('dashboard.systemStatus')}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">{t('dashboard.allSystemsOperational')}</div>
              <div className="text-sm text-gray-600 mt-1">{t('dashboard.everythingRunning')}</div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">{t('dashboard.uptime')}</div>
              <div className="text-sm text-gray-600 mt-1">{t('dashboard.last30days')}</div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">
                {hasCmmsSubscription || hasChatbotSubscription ? t('dashboard.activeSubscription') : t('dashboard.noActivePlan')}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {hasCmmsSubscription && hasChatbotSubscription
                  ? `${t('nav.cmms')} + ${t('nav.chatbot')}`
                  : hasCmmsSubscription
                  ? t('nav.cmms')
                  : hasChatbotSubscription
                  ? t('nav.chatbot')
                  : t('dashboard.subscribeNow')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
